import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';

import { useApolloClient } from '@apollo/client';
import TextInput from '../../../TextInput/TextInput';
import PasswordInput from '../../../PasswordInput/PasswordInput';
import * as Styled from './SignUpForm.styled';
import Button from '../../../Button/Button';
import { platform_base_url } from '../../../../util/config';
import { post } from '../../../../util/request';
import { completeAuthentication } from '../../Authentication.utils';
import Feature from '../../../../util/feature';
import FacebookLoginButton from '../FacebookLoginButton/FacebookLoginButton';
import { SocialContainer } from '../AuthenticationBody/AuthenticationBody.styled';
import GoogleSignInButton from '../GoogleSignInButton/GoogleSignInButton';
import { LINKS } from './SignUpForm.utils';

const SignUpSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address.')
    .required('Required'),
  first_name: Yup.string('Please enter your first name.').required('Required'),
  last_name: Yup.string('Please enter your last name.').required('Required')
});

const PASSWORD_RESET_REQUIRED = 303;
const CUSTOMER_ALREADY_EXISTS = 409;

/**
 * `SignUpForm` allows a user to create a Bombas account.
 */
const SignUpForm = ({
  onComplete,
  onLoginClick,
  onPasswordResetRequired,
  perks
}) => {
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState(false);

  const handleSubmit = async (
    values,
    { setFieldError, validateForm, setErrors }
  ) => {
    setIsLoading(true);
    setServerError(false);

    const formErrors = await validateForm();
    if (Object.keys(formErrors).length) {
      setErrors(formErrors);
      setIsLoading(false);
      return;
    }

    await post(`${platform_base_url}/v2/identity/create`, values, {
      credentials: 'include'
    })
      .then(completeAuthentication.bind(null, client))
      .then(onComplete.bind(null, { id: values.email, method: 'bombas' }))
      .catch((e) => {
        const error = e?.response?.errors?.[0];

        if (
          error?.status === PASSWORD_RESET_REQUIRED ||
          error?.status === CUSTOMER_ALREADY_EXISTS
        ) {
          post(`${platform_base_url}/v2/identity/forgot-password`, {
            email: values.email
          })
            .then(() => {
              onPasswordResetRequired(values.email);
            })
            .catch(() => {
              setServerError(true);
            });
        } else if (e.response?.errors?.length) {
          e.response.errors.forEach((item) => {
            setFieldError(item.field, item.title);
          });

          window.DD_RUM?.addError(e, { location: 'identity/create' });
        }
        setIsLoading(false);
      });
  };

  return (
    <Styled.SignUpForm>
      <Styled.Text>
        Create an account for{' '}
        {perks && (
          <>
            <Styled.Emphasis>{perks.join(', ')}</Styled.Emphasis>,{' '}
          </>
        )}
        order updates and more!
      </Styled.Text>
      <Formik
        initialValues={{
          email: '',
          first_name: '',
          last_name: '',
          password: ''
        }}
        validationSchema={SignUpSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
      >
        <Form>
          <Styled.FormWrap>
            <TextInput
              id="signup_email"
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
            />
            <TextInput
              id="signup_first"
              label="First Name"
              name="first_name"
              autoComplete="name"
            />
            <TextInput
              id="signup_last"
              label="Last Name"
              name="last_name"
              autoComplete="name"
            />
            <PasswordInput
              id="signup_pass"
              name="password"
              label="Password"
              autoComplete="new-password"
              withRequirements
              validateOnChange
            />
            {serverError && (
              <Styled.NotificationError
                variant="error"
                title="Something went wrong on our end."
                body="Please try submitting the form again."
                isFull
                onRequestClose={() => setServerError(false)}
              />
            )}
            <Button
              type="submit"
              variant="primary"
              isFull
              size="large"
              isLoading={isLoading}
            >
              Sign Up
            </Button>
          </Styled.FormWrap>
        </Form>
      </Formik>
      <Styled.OrText>or</Styled.OrText>

      <SocialContainer>
        <FacebookLoginButton
          onComplete={onComplete}
          setIsLoading={setIsLoading}
        />
        <GoogleSignInButton
          onComplete={onComplete}
          setIsLoading={setIsLoading}
        />
      </SocialContainer>

      <Styled.DisclaimerText>
        {Feature.isEnabled('auto_email_marketing_opt_in') ? (
          <>
            By selecting &quot;Sign Up,&quot; you are giving us permission to
            include you in Bombas emails and Email-Based Advertising. For
            additional information, please see our{' '}
          </>
        ) : (
          <>By selecting &quot;Sign Up,&quot; I agree to the Bombas </>
        )}{' '}
        {LINKS.termsOfUse} and {LINKS.privacyPolicy}.
      </Styled.DisclaimerText>
      <Styled.LoginText>
        Already Have An Account?{' '}
        <Styled.LoginLink onClick={onLoginClick}>Log In</Styled.LoginLink>
      </Styled.LoginText>
    </Styled.SignUpForm>
  );
};

SignUpForm.propTypes = {
  /**
   * Callback on successful submission of sign up form
   */
  onComplete: PropTypes.func.isRequired,
  /**
   * Callback for click on login link
   */
  onLoginClick: PropTypes.func.isRequired,
  /**
   * Callback function to run when a password reset is required to authenticate
   */
  onPasswordResetRequired: PropTypes.func.isRequired,
  /**
   * Perks that are offered by signing up
   * */
  perks: PropTypes.arrayOf(PropTypes.string)
};

SignUpForm.defaultProps = {
  perks: null
};

SignUpForm.displayName = 'SignUpForm';

export default SignUpForm;
